import React, {Component} from 'react';
import {action, observable} from 'mobx';
import {inject, observer} from 'mobx-react';
import _ from "underscore";
import {FormInput, FormSelect, FormCheckbox, Button} from "shards-react";
import {toast} from "react-toastify";
import PageTitle from "../../../components/common/PageTitle";
import FeatherIcon from 'feather-icons-react';
import '../Widget/style.scss';
import './freeShippingWidget.scss';

// 무료배송 안내 위젯 (주문유도)
// - 카페24 배송비 설정(GET /api/v2/admin/shipping)을 조회해 무료배송 기준 금액을 자동 감지
// - 쇼핑몰 위젯(상품 상세/장바구니)에서 장바구니 금액 기반 "얼마 더 담으면 무료배송" 배너 노출
// - 설정은 metaWidget.freeShipping / freeShippingConfig 로 저장 (스킨 위젯이 totalConfig 로 수신)
// - UI 는 리뷰 위젯 > 페이지별 위젯(widgetByPage)의 아코디언(wbp-*) + s-table 규약을 따른다.

const SHIPPING_TYPE_LABELS = {
  T: '배송비 무료',
  R: '고정 배송비',
  M: '구매 금액에 따른 부과 (조건부 무료)',
  D: '구매 금액별 차등 배송비',
  W: '상품 무게별 차등 배송비',
  C: '상품 수량별 차등 배송비',
  N: '상품 수량에 비례하여 부과'
};

const nf = (n) => new Intl.NumberFormat('ko-KR').format(Math.round(Number(n) || 0));

@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@inject(({ common }) => ({ getStoreTotalConfig: common.getStoreTotalConfig, updateDoc: common.updateDoc, getDoc: common.getDoc }))
@observer
class FreeShippingWidget extends Component {

  @observable isLogin = false;
  @observable isLoadingShipping = false;
  @observable isSaving = false;
  @observable shipping = null;          // 카페24 배송 설정 원본
  @observable shippingError = '';
  @observable previewMode = 'progress'; // progress | achieved
  @observable openSection = 'freeShipping';
  @observable activePageTab = 'product'; // 페이지별 설정 탭 (product | cart)
  @observable isDirty = false;          // 미저장 변경 여부 (widgetByPage 규약: dirty dot + sticky 저장 바)
  @observable themeColor = '#1D4ED8';   // 리뷰 설정(디자인)의 테마 폰트 색상 — 포인트 색상 미설정 시 기본값
  _snapshot = null;                     // 되돌리기용 로드/저장 시점 스냅샷

  @action
  toggleAccordion = (key) => {
    this.openSection = this.openSection === key ? null : key;
  };

  takeSnapshot = () => {
    this._snapshot = JSON.parse(JSON.stringify({
      freeShipping: this.metaWidget.freeShipping,
      freeShippingConfig: this.metaWidget.freeShippingConfig
    }));
  };

  @action markDirty = () => { this.isDirty = true; };

  @action revert = () => {
    if(!this._snapshot) return;
    this.metaWidget.freeShipping = this._snapshot.freeShipping;
    this.metaWidget.freeShippingConfig = JSON.parse(JSON.stringify(this._snapshot.freeShippingConfig));
    this.isDirty = false;
  };

  @observable metaWidget = {
    store_id: null,
    freeShipping: "no",
    freeShippingConfig: {
      pages: ['product', 'cart'],
      freeShippingAmount: 0,
      baseShippingFee: 0,
      // 노출 방식: card(지정 위치 카드형) | bar(화면 고정 바형)
      displayType: 'card',
      // 카드형 — 페이지별 설정 (상세/장바구니 각각: 삽입 선택자 PC/모바일, 삽입 방법, 추천 상품 캐러셀)
      pageSettings: {
        product: { parentSelector: '', parentSelectorMobile: '', insertPosition: 'prepend', showRecommend: true, recommendCount: 10 },
        cart:    { parentSelector: '', parentSelectorMobile: '', insertPosition: 'prepend', showRecommend: true, recommendCount: 10 }
      },
      // 바형 — 고정 위치
      position: 'bottom',
      messageProgress: '{remain}원만 더 담으면 무료배송!',
      messageAchieved: '무료배송 가능!',
      // accentColor 미설정('') 시 리뷰 설정의 테마 폰트 색상(themeColor)을 따름 — 위젯 런타임에서도 동일 폴백
      design: { bgColor: '#16202D', textColor: '#FFFFFF', accentColor: '' }
    }
  };

  constructor(props) {
    super(props);
    this.init();
  }

  @action
  init = async () => {
    if(!this.props.isLogin) return;
    let _this = this;
    this.isLogin = this.props.isLogin;
    this.teamUser = this.props.me;
    this.store = this.props.me.store ? this.props.me.store : {};

    this.props.getStoreTotalConfig({store_id: this.teamUser.selStore_id, shopNo: this.teamUser.selShopNo ? this.teamUser.selShopNo : 1}, function(res) {
      // 리뷰 설정(디자인)의 테마 폰트 색상 — 포인트 색상 기본값으로 사용
      if(res && res.basicSettings && res.basicSettings.themeColor) {
        _this.themeColor = res.basicSettings.themeColor;
      }
      if(res && res.metaWidget) {
        // 저장된 config 를 기본값 위에 병합 (누락 키는 기본값 유지)
        const saved = {...(res.metaWidget.freeShippingConfig || {})};

        // 페이지별 설정 마이그레이션 — 구(단일) parentSelector/insertPosition/showRecommend 값을
        // pageSettings 가 없으면 상세/장바구니 양쪽의 초기값으로 승계
        const legacyBase = {
          parentSelector: saved.parentSelector || '',
          parentSelectorMobile: saved.parentSelectorMobile || '',
          insertPosition: saved.insertPosition || 'prepend',
          showRecommend: saved.showRecommend !== false,
          recommendCount: saved.recommendCount || 10
        };
        const savedPs = saved.pageSettings || {};
        saved.pageSettings = {
          product: _.defaults({...(savedPs.product || {})}, legacyBase),
          cart: _.defaults({...(savedPs.cart || {})}, legacyBase)
        };

        _this.metaWidget.freeShipping = res.metaWidget.freeShipping || 'no';
        _this.metaWidget.freeShippingConfig = _.defaults({...saved, design: _.defaults(saved.design || {}, _this.metaWidget.freeShippingConfig.design)}, _this.metaWidget.freeShippingConfig);
        _this.metaWidget.store_id = res.metaWidget.store_id;
      }
      _this.takeSnapshot();
      // 진입 시 배송 정보 자동 조회
      _this.fetchShipping();
    });
  };

  // === 카페24 배송 설정 조회 ===
  @observable isScopeError = false;   // 앱 권한(mall.read_shipping) 미동의 여부

  @action
  fetchShipping = () => {
    if(this.isLoadingShipping) return;
    let _this = this;
    this.isLoadingShipping = true;
    this.shippingError = '';
    this.isScopeError = false;
    this.props.getDoc('getCafe24Shipping', {
      store_id: this.teamUser.selStore_id,
      shopNo: this.teamUser.selShopNo ? this.teamUser.selShopNo : 1
    }).then((res) => {
      _this.isLoadingShipping = false;
      if(res && res.errors && res.errors.length > 0) {
        _this.shippingError = res.errors[0].message;
        return;
      }
      const data = res && res.data ? res.data : null;
      // 백엔드가 data.error 로 카페24 에러를 전달 (insufficient_scope 등)
      if(data && data.error) {
        const msg = data.error.message || '배송 설정을 불러오지 못했습니다.';
        if(data.error.code === 'insufficient_scope' || msg.indexOf('insufficient_scope') > -1 || msg.indexOf('permission') > -1) {
          _this.isScopeError = true;
          _this.shippingError = '앱에 배송 조회 권한(mall.read_shipping)이 없습니다.';
        } else {
          _this.shippingError = msg;
        }
        return;
      }
      // 유효한 배송 설정인지 확인 (배포 전 서버 등에서 빈 객체가 올 수 있음)
      if(data && data.shipping_fee_type !== undefined) {
        _this.shipping = data;
        // 감지된 무료배송 기준 금액을 설정에 자동 입력 (기존 값과 다를 때만)
        const det = _this.detectThreshold(data);
        if(det && det.type === 'threshold') {
          const cfg = _this.metaWidget.freeShippingConfig;
          if(Number(cfg.freeShippingAmount) !== det.amount) {
            cfg.freeShippingAmount = det.amount;
            if(det.fee > 0) cfg.baseShippingFee = det.fee;
            _this.markDirty();
            toast.info('배송 정보에서 무료배송 기준 금액 ' + nf(det.amount) + '원이 자동 입력되었습니다. 저장을 눌러 반영해 주세요.');
          }
        }
      } else {
        _this.shipping = null;
        _this.shippingError = '배송 설정을 불러오지 못했습니다. 잠시 후 다시 조회해 주세요.';
      }
    }).catch((e) => {
      _this.isLoadingShipping = false;
      _this.shippingError = (e && e.message) || '배송 설정 조회 중 오류가 발생했습니다.';
    });
  };

  // === 배송 설정에서 무료배송 기준 금액 자동 감지 ===
  detectThreshold = (s) => {
    if(!s) return null;
    if(s.shipping_fee_type === 'T') return { type: 'always', amount: 0 };
    if(s.shipping_fee_type === 'M') {
      // 조건부 무료: N원 이상 무료 — 카페24 응답 필드 케이스 모두 시도
      const v = parseFloat(s.min_price || s.free_shipping_price || s.shipping_fee_criteria_amount || 0);
      if(v > 0) return { type: 'threshold', amount: v, fee: parseFloat(s.shipping_fee || 0) };
      return null;
    }
    if(s.shipping_fee_type === 'D' && Array.isArray(s.shipping_rates)) {
      // 구매 금액별 차등: 배송비 0원 구간의 최소 금액이 무료배송 시작점
      const freeMins = s.shipping_rates
        .filter(r => parseFloat(r.shipping_fee) === 0)
        .map(r => parseFloat(r.min_value))
        .sort((a, b) => a - b);
      if(freeMins.length > 0) {
        const fees = s.shipping_rates.map(r => parseFloat(r.shipping_fee)).filter(f => f > 0);
        return { type: 'threshold', amount: freeMins[0], fee: fees.length ? Math.max(...fees) : 0 };
      }
      return null;
    }
    return null;
  };

  // === 설정 변경 핸들러 ===
  @action setUse = (v) => { this.metaWidget.freeShipping = v; this.markDirty(); };

  @action togglePage = (key) => {
    const pages = this.metaWidget.freeShippingConfig.pages || [];
    if(pages.includes(key)) this.metaWidget.freeShippingConfig.pages = pages.filter(p => p !== key);
    else this.metaWidget.freeShippingConfig.pages = [...pages, key];
    this.markDirty();
  };

  @action onChangeConfig = (key, value, type) => {
    this.metaWidget.freeShippingConfig[key] = type === 'number' ? (value === '' ? 0 : parseInt(value, 10) || 0) : value;
    this.markDirty();
  };

  @action onChangeDesign = (key, value) => {
    this.metaWidget.freeShippingConfig.design = { ...this.metaWidget.freeShippingConfig.design, [key]: value };
    this.markDirty();
  };

  // 페이지별(상세/장바구니) 설정 변경
  @action onChangePageSetting = (pageKey, key, value, type) => {
    const ps = this.metaWidget.freeShippingConfig.pageSettings || {};
    const cur = ps[pageKey] || {};
    this.metaWidget.freeShippingConfig.pageSettings = {
      ...ps,
      [pageKey]: { ...cur, [key]: type === 'number' ? (value === '' ? 0 : parseInt(value, 10) || 0) : value }
    };
    this.markDirty();
  };

  // === 저장 ===
  save = () => {
    if(this.isSaving) return;
    const cfg = this.metaWidget.freeShippingConfig;
    if(this.metaWidget.freeShipping === 'yes' && (!cfg.freeShippingAmount || cfg.freeShippingAmount <= 0)) {
      toast.error('무료배송 기준 금액을 입력해 주세요. (배송 정보 조회의 자동 감지를 활용할 수 있습니다)');
      return;
    }
    let _this = this;
    this.isSaving = true;
    this.props.updateDoc('updateMetaWidget', {
      store_id: this.store._id,
      shopNo: this.teamUser.selShopNo ? this.teamUser.selShopNo : 1,
      freeShipping: this.metaWidget.freeShipping,
      freeShippingConfig: {...cfg}
    }, function(res) {
      _this.isSaving = false;
      if(res && res.errors && res.errors.length > 0) {
        toast.error(res.errors[0].message);
      } else {
        toast.success('무료배송 위젯 설정이 저장되었습니다.');
        _this.isDirty = false;
        _this.takeSnapshot();
      }
    });
  };

  // === 미리보기 문구 치환 ===
  previewMessage = () => {
    const cfg = this.metaWidget.freeShippingConfig;
    const amount = cfg.freeShippingAmount || 50000;
    const sampleCart = Math.round(amount * 0.6);
    if(this.previewMode === 'achieved') return (cfg.messageAchieved || '');
    return (cfg.messageProgress || '')
      .replace('{remain}', nf(amount - sampleCart))
      .replace('{amount}', nf(amount));
  };

  // 입력 하단 힌트 (widgetByPage 규약과 동일한 톤)
  renderHint = (children) => (
    <p style={{marginTop: 5, marginBottom: 0, fontSize: 12, color: '#6B7280'}}>{children}</p>
  );

  render() {
    const isUse = this.metaWidget.freeShipping === 'yes';
    const cfg = this.metaWidget.freeShippingConfig;
    const design = cfg.design || {};
    // 포인트 색상 — 미설정 시 리뷰 설정(디자인)의 테마 폰트 색상을 따름
    const accent = design.accentColor || this.themeColor;
    const s = this.shipping;
    const detected = this.detectThreshold(s);
    const amount = cfg.freeShippingAmount || 0;
    const sampleCart = Math.round((amount || 50000) * 0.6);
    const progressPer = this.previewMode === 'achieved' ? 100 : Math.min(100, Math.round(sampleCart / (amount || 50000) * 100));
    const isCard = (cfg.displayType || 'card') === 'card';
    // 미리보기 — 노출 페이지 중 첫 페이지(상세 우선)의 페이지별 설정 기준
    const previewPageKey = (cfg.pages || []).includes('product') ? 'product' : 'cart';
    const previewPs = (cfg.pageSettings && cfg.pageSettings[previewPageKey]) || {};
    const previewShowRecommend = previewPs.showRecommend !== false;

    return (
      <div className="main-content fsw-page">
        <PageTitle/>

        <div className="widget-box">

          <div className="widget-page-tabs">
            <div className="page-tab-header">
              <h3>무료배송 안내 위젯</h3>
              <p>고객의 장바구니 금액을 기준으로 "얼마를 더 담으면 무료배송"인지 안내해 추가 구매를 유도합니다</p>
            </div>
          </div>

          {/* === 무료배송 안내 위젯 — 배송 정보 + 설정 + 미리보기 통합 === */}
          <div className={`wbp-accordion ${this.openSection === 'freeShipping' ? 'is-open' : ''}`} style={{marginTop: 16}}>
            <div className="wbp-accordion__head" onClick={() => this.toggleAccordion('freeShipping')}>
              <div className="wbp-accordion__title">
                <FeatherIcon icon="truck" size="16" />
                <span>무료배송 안내 위젯</span>
                <span className={`wbp-status-chip ${isUse ? 'is-on' : 'is-off'}`}>{isUse ? 'ON' : 'OFF'}</span>
                {detected && detected.type === 'threshold' && (
                  <span className="wbp-status-chip is-on">{nf(detected.amount)}원 이상 무료</span>
                )}
                {this.isDirty && <span className="wbp-dirty-dot" title="미저장 변경사항"></span>}
              </div>
              <div className="wbp-accordion__toggle">
                <FeatherIcon icon={this.openSection === 'freeShipping' ? 'chevron-up' : 'chevron-down'} size="18" />
              </div>
            </div>
            <div className="wbp-accordion__body" style={{display: this.openSection === 'freeShipping' ? 'block' : 'none'}}>
              <div className="section-box" style={{marginTop: 0, border: 'none', boxShadow: 'none', padding: '4px 4px 12px'}}>
                <table className="s-table">
                  <colgroup><col width="200px"/><col /></colgroup>
                  <tbody>
                  {/* 쇼핑몰 배송 정보 (카페24 조회 → 기준 금액 자동 입력) */}
                  <tr>
                    <th>쇼핑몰 배송 정보
                      <span className="wbp-help" title="카페24에 설정된 배송비 정보를 조회해 무료배송 기준 금액을 자동 입력합니다.">
                        <FeatherIcon icon="help-circle" size="13" />
                      </span>
                    </th>
                    <td className="text-left">
                      {this.shippingError && !this.isScopeError && (
                        <div className="fsw-alert fsw-alert--error" style={{marginTop: 0}}>
                          <FeatherIcon icon="alert-circle" size="14"/> {this.shippingError}
                        </div>
                      )}

                      {this.isScopeError && (
                        <div className="fsw-alert fsw-alert--warn fsw-alert--column" style={{marginTop: 0}}>
                          <div className="fsw-alert__row">
                            <FeatherIcon icon="lock" size="14"/>
                            <span><strong>배송 조회 권한이 아직 승인되지 않았습니다.</strong> (insufficient_scope)</span>
                          </div>
                          <ul className="fsw-alert__list">
                            <li>온리플 앱에 배송 정보 조회 권한이 추가되어, 쇼핑몰 관리자에서 <strong>온리플 앱 재접속 시 추가 권한 동의</strong>가 필요합니다.</li>
                            <li>카페24 관리자 → 앱스토어 → 마이앱 → <strong>온리플 실행</strong> 시 표시되는 권한 동의 화면에서 동의해 주세요.</li>
                            <li>동의 전에도 아래 <strong>무료배송 기준 금액을 직접 입력</strong>하면 위젯은 정상 동작합니다.</li>
                          </ul>
                        </div>
                      )}

                      {this.isLoadingShipping && <div className="fsw-loading" style={{padding: '6px 0', textAlign: 'left'}}>배송 정보를 불러오는 중...</div>}

                      {s && !this.isLoadingShipping && (
                        <>
                          <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                            <strong>{SHIPPING_TYPE_LABELS[s.shipping_fee_type] || s.shipping_fee_type || '-'}</strong>
                            <span style={{color: '#94A3B8'}}>·</span>
                            <span>기본 배송비 {s.shipping_fee !== undefined && s.shipping_fee !== null ? nf(s.shipping_fee) + '원' : '-'}</span>
                            <span style={{color: '#94A3B8'}}>·</span>
                            <span>배송 {s.shipping_period ? (s.shipping_period.minimum + '~' + s.shipping_period.maximum + '일') : '-'}</span>
                            <button type="button" className="wbp-accordion__sample-btn"
                                    onClick={this.fetchShipping}
                                    title="카페24 배송 설정 다시 조회">
                              <FeatherIcon icon="refresh-cw" size="13" /> 다시 조회
                            </button>
                          </div>

                          {Array.isArray(s.shipping_rates) && s.shipping_rates.length > 0 && (
                            <div className="fsw-rates" style={{marginTop: 10, maxWidth: 480}}>
                              <table className="fsw-rates__table">
                                <thead>
                                  <tr><th>구매 금액</th><th>배송비</th></tr>
                                </thead>
                                <tbody>
                                  {s.shipping_rates.map((r, i) => (
                                    <tr key={i} className={parseFloat(r.shipping_fee) === 0 ? 'is-free' : ''}>
                                      <td>{nf(r.min_value)}원 ~ {r.max_value ? nf(r.max_value) + '원' : ''}</td>
                                      <td>{parseFloat(r.shipping_fee) === 0 ? <strong>무료</strong> : nf(r.shipping_fee) + '원'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {detected && detected.type === 'threshold' && (
                            <div className="fsw-alert fsw-alert--detect">
                              <FeatherIcon icon="zap" size="14"/>
                              <span><strong>{nf(detected.amount)}원 이상</strong> 구매 시 무료배송 — 아래 기준 금액에 <strong>자동 입력</strong>됩니다.</span>
                            </div>
                          )}
                          {detected && detected.type === 'always' && (
                            <div className="fsw-alert fsw-alert--warn">
                              <FeatherIcon icon="info" size="14"/> 이 쇼핑몰은 <strong>전체 무료배송</strong>으로 설정되어 있어 위젯이 필요하지 않을 수 있습니다.
                            </div>
                          )}
                          {!detected && (
                            <div className="fsw-alert fsw-alert--warn">
                              <FeatherIcon icon="info" size="14"/> 무료배송 기준 금액을 자동 감지하지 못했습니다. 아래 기준 금액을 <strong>직접 입력</strong>해 주세요.
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>무료배송 안내 위젯</th>
                    <td className="text-left">
                      <FormCheckbox toggle small
                                    checked={isUse}
                                    onChange={() => this.setUse(isUse ? 'no' : 'yes')}>
                        {isUse ? '사용함' : '사용안함'}
                      </FormCheckbox>
                    </td>
                  </tr>
                  <tr>
                    <th>노출 페이지</th>
                    <td className="text-left">
                      <div style={{display: 'flex', gap: 24, flexWrap: 'wrap'}}>
                        <FormCheckbox toggle small
                                      checked={(cfg.pages || []).includes('product')}
                                      onChange={() => this.togglePage('product')}>
                          상품 상세페이지
                        </FormCheckbox>
                        <FormCheckbox toggle small
                                      checked={(cfg.pages || []).includes('cart')}
                                      onChange={() => this.togglePage('cart')}>
                          장바구니 페이지
                        </FormCheckbox>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>무료배송 기준 금액</th>
                    <td className="text-left">
                      <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <FormInput type="number" value={cfg.freeShippingAmount || ''}
                                   placeholder="예: 50000"
                                   onChange={(e) => this.onChangeConfig('freeShippingAmount', e.target.value, 'number')}
                                   style={{maxWidth: 160}}/>
                        <span>원 이상 구매 시 무료배송</span>
                      </div>
                      {this.renderHint(<>위 <strong>쇼핑몰 배송 정보</strong>에서 감지된 기준 금액이 자동 입력됩니다. 필요 시 직접 수정할 수 있습니다.</>)}
                    </td>
                  </tr>
                  <tr>
                    <th>기본 배송비 (표시용)</th>
                    <td className="text-left">
                      <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <FormInput type="number" value={cfg.baseShippingFee || ''}
                                   placeholder="예: 3000"
                                   onChange={(e) => this.onChangeConfig('baseShippingFee', e.target.value, 'number')}
                                   style={{maxWidth: 160}}/>
                        <span>원 (선택)</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>노출 방식</th>
                    <td className="text-left">
                      <FormSelect value={cfg.displayType || 'card'}
                                  onChange={(e) => this.onChangeConfig('displayType', e.target.value)}
                                  style={{maxWidth: 280}}>
                        <option value="card">지정 위치 카드형 (추천 상품 캐러셀 포함)</option>
                        <option value="bar">화면 고정 바형</option>
                      </FormSelect>
                    </td>
                  </tr>
                  {/* 카드형 — 페이지별(상세/장바구니) 설정: 탭 토글로 한 페이지씩 표시 */}
                  {isCard && (() => {
                    const enabled = ['product', 'cart'].filter(k => (cfg.pages || []).includes(k));
                    if (enabled.length === 0) return null;
                    const pageKey = enabled.includes(this.activePageTab) ? this.activePageTab : enabled[0];
                    const ps = (cfg.pageSettings && cfg.pageSettings[pageKey]) || {};
                    return (
                      <React.Fragment>
                        <tr>
                          <td colSpan="2" className="text-left"
                              style={{background: '#F8F9FC', padding: '10px 12px'}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                              <span style={{fontWeight: 700, color: '#475569', fontSize: 12.5, marginRight: 4}}>페이지별 설정</span>
                              {enabled.map(k => (
                                <button key={k} type="button"
                                        className={'fsw-chip' + (pageKey === k ? ' is-active' : '')}
                                        onClick={() => { this.activePageTab = k; }}>
                                  <FeatherIcon icon={k === 'product' ? 'shopping-bag' : 'shopping-cart'} size="12"
                                               style={{marginRight: 4, verticalAlign: '-2px'}}/>
                                  {k === 'product' ? '상품 상세페이지' : '장바구니 페이지'}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <th>부모 클래스/ID (PC)</th>
                          <td className="text-left">
                            <FormInput value={ps.parentSelector || ''}
                                       placeholder=".class-name 또는 #element-id"
                                       onChange={(e) => this.onChangePageSetting(pageKey, 'parentSelector', e.target.value)}
                                       style={{maxWidth: 420}}/>
                            {this.renderHint(pageKey === 'product'
                              ? <>카드를 삽입할 요소 — 미설정 시 기본값 <code>#totalProducts</code> <strong>요소 이전</strong>에 삽입됩니다.</>
                              : <>카드를 삽입할 요소 — 미설정 시 기본값 <code>.ec-base-step</code> <strong>요소 맨 뒤</strong>에 삽입됩니다.</>)}
                          </td>
                        </tr>
                        <tr>
                          <th>부모 클래스/ID (모바일)</th>
                          <td className="text-left">
                            <FormInput value={ps.parentSelectorMobile || ''}
                                       placeholder=".class-name 또는 #element-id"
                                       onChange={(e) => this.onChangePageSetting(pageKey, 'parentSelectorMobile', e.target.value)}
                                       style={{maxWidth: 420}}/>
                            {this.renderHint('미입력 시 PC 선택자를 사용합니다.')}
                          </td>
                        </tr>
                        <tr>
                          <th>삽입 방법</th>
                          <td className="text-left">
                            <FormSelect value={ps.insertPosition || 'prepend'}
                                        onChange={(e) => this.onChangePageSetting(pageKey, 'insertPosition', e.target.value)}
                                        style={{maxWidth: 200}}>
                              <option value="prepend">요소 맨 앞</option>
                              <option value="append">요소 맨 뒤</option>
                              <option value="before">요소 이전</option>
                              <option value="after">요소 다음</option>
                            </FormSelect>
                            {this.renderHint('미설정 시 페이지별 기본 위치에 삽입되며, 지정/기본 선택자를 모두 찾지 못하면 화면 고정 바형으로 자동 폴백되어 위젯이 항상 노출됩니다.')}
                          </td>
                        </tr>
                        <tr>
                          <th>추천 상품 캐러셀</th>
                          <td className="text-left">
                            <FormCheckbox toggle small
                                          checked={ps.showRecommend !== false}
                                          onChange={() => this.onChangePageSetting(pageKey, 'showRecommend', !(ps.showRecommend !== false))}>
                              {ps.showRecommend !== false ? '사용함' : '사용안함'}
                            </FormCheckbox>
                            {ps.showRecommend !== false && (
                              <div style={{display: 'flex', alignItems: 'center', gap: 8, marginTop: 8}}>
                                <FormInput type="number" value={ps.recommendCount || ''}
                                           placeholder="10"
                                           onChange={(e) => this.onChangePageSetting(pageKey, 'recommendCount', e.target.value, 'number')}
                                           style={{maxWidth: 100}}/>
                                <span>개 노출</span>
                              </div>
                            )}
                            {this.renderHint('저가순으로 노출되어 "조금만 더 담기"를 유도합니다. 담기 클릭 시 옵션/수량 선택 후 장바구니에 바로 담기며, 게이지가 실시간 갱신됩니다.')}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })()}
                  {!isCard && (
                    <tr>
                      <th>배너 위치</th>
                      <td className="text-left">
                        <FormSelect value={cfg.position || 'bottom'}
                                    onChange={(e) => this.onChangeConfig('position', e.target.value)}
                                    style={{maxWidth: 200}}>
                          <option value="bottom">하단 고정</option>
                          <option value="top">상단 고정</option>
                        </FormSelect>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <th>문구 — 달성 전</th>
                    <td className="text-left">
                      <FormInput value={cfg.messageProgress || ''}
                                 onChange={(e) => this.onChangeConfig('messageProgress', e.target.value)}
                                 style={{maxWidth: 420}}/>
                      {this.renderHint(<><code>{'{remain}'}</code> 남은 금액 · <code>{'{amount}'}</code> 기준 금액</>)}
                    </td>
                  </tr>
                  <tr>
                    <th>문구 — 달성 시</th>
                    <td className="text-left">
                      <FormInput value={cfg.messageAchieved || ''}
                                 onChange={(e) => this.onChangeConfig('messageAchieved', e.target.value)}
                                 style={{maxWidth: 420}}/>
                    </td>
                  </tr>
                  <tr>
                    <th>색상</th>
                    <td className="text-left">
                      <div style={{display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap'}}>
                        {(isCard
                          ? [{k: 'accentColor', l: '포인트(제목·게이지)'}]
                          : [{k: 'bgColor', l: '배경'}, {k: 'textColor', l: '글자'}, {k: 'accentColor', l: '포인트(게이지)'}]
                        ).map(c => (
                          <label key={c.k} className="fsw-color">
                            <input type="color"
                                   value={c.k === 'accentColor' ? accent : (design[c.k] || '#000000')}
                                   onChange={(e) => this.onChangeDesign(c.k, e.target.value)}/>
                            <span>{c.l}</span>
                          </label>
                        ))}
                        {design.accentColor && (
                          <Button size="sm" theme="light" onClick={() => this.onChangeDesign('accentColor', '')}>
                            테마 색상으로 초기화
                          </Button>
                        )}
                      </div>
                      {this.renderHint(<>포인트 색상 미설정 시 <strong>리뷰 설정 &gt; 디자인의 테마 폰트 색상</strong>({this.themeColor})을 따릅니다.</>)}
                    </td>
                  </tr>
                  {/* 미리보기 */}
                  <tr>
                    <th>미리보기</th>
                    <td className="text-left">
                      <div style={{maxWidth: 820}}>
                        <div className="fsw-preview-tabs">
                    {[{v: 'progress', l: '달성 전'}, {v: 'achieved', l: '달성'}].map(t => (
                      <button key={t.v} type="button"
                              className={'fsw-chip' + (this.previewMode === t.v ? ' is-active' : '')}
                              onClick={() => { this.previewMode = t.v; }}>{t.l}</button>
                    ))}
                  </div>
                  {isCard ? (
                    <div className="fsw-preview-frame fsw-preview-frame--card">
                      <div className="fsw-preview-card">
                        {/* 제목 + 페이징 넘버링 (같은 라인) */}
                        <div className="fsw-preview-card__head">
                          <div className="fsw-preview-card__title" style={{color: accent}}>{this.previewMessage()}</div>
                          {previewShowRecommend && <span className="fsw-preview-card__count">1/4</span>}
                        </div>
                        <div className="fsw-preview-card__bar">
                          <div className="fsw-preview-card__bar-fill" style={{width: progressPer + '%', background: accent}}/>
                        </div>
                        {previewShowRecommend && (
                          <div className="fsw-preview-card__list-wrap">
                            <div className="fsw-preview-card__items">
                              {[
                                {n: '고단백 저당 통밀 스콘 3개 (플레인/초코)', o: 15900, p: 8100, d: 49},
                                {n: '저당 베이글 4개 세트', o: 14000, p: 8000, d: 43},
                                {n: '통밀식빵 3종 2개입 1set', o: 10700, p: 4700, d: 56}
                              ].map((p, i) => (
                                <div key={i} className="fsw-preview-item">
                                  <div className="fsw-preview-item__img"><FeatherIcon icon="image" size="18"/></div>
                                  <div className="fsw-preview-item__info">
                                    <div className="fsw-preview-item__name">{p.n}</div>
                                    <div className="fsw-preview-item__org">{nf(p.o)}원</div>
                                    <div className="fsw-preview-item__price"><em>{p.d}%</em> {nf(p.p)}원</div>
                                    <button type="button" className="fsw-preview-item__add">담기</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {/* 오른쪽 페이드 + 사이드 nav (수직 중앙) */}
                            <span className="fsw-preview-card__fade"/>
                            <button type="button" className="fsw-preview-card__nav" aria-label="다음">&rsaquo;</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="fsw-preview-frame">
                      <div className="fsw-preview-skeleton">
                        <div className="sk-img"/><div className="sk-line"/><div className="sk-line short"/>
                      </div>
                      <div className={'fsw-preview-banner' + (cfg.position === 'top' ? ' at-top' : '')}
                           style={{background: design.bgColor, color: design.textColor}}>
                        <div className="fsw-preview-banner__row">
                          <span className="fsw-preview-banner__icon" role="img" aria-label="배송">🚚</span>
                          <span className="fsw-preview-banner__msg">{this.previewMessage()}</span>
                        </div>
                        <div className="fsw-preview-banner__bar">
                          <div className="fsw-preview-banner__bar-fill" style={{width: progressPer + '%', background: accent}}/>
                        </div>
                      </div>
                    </div>
                  )}
                        <p className="fsw-preview-hint">
                          <FeatherIcon icon="info" size="11"/>
                          로그인 고객의 장바구니 금액은 카페24 Front SDK로 실시간 조회되고, 비로그인 고객은 장바구니 페이지의 총 상품금액 기준으로 달성/미달성을 안내합니다.
                        </p>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>

        {/* === Sticky 저장 바 (widgetByPage 규약 — dirty 있을 때만 노출) === */}
        {this.isDirty && (
          <div className="wbp-sticky-save wbp-sticky-save--top">
            <div className="wbp-sticky-save__msg">
              <FeatherIcon icon="alert-circle" size="16" />
              <span><strong>무료배송 안내 위젯</strong>에 저장되지 않은 변경사항이 있습니다.</span>
            </div>
            <div className="wbp-sticky-save__actions">
              <Button theme="light" size="sm" onClick={this.revert}>되돌리기</Button>
              <Button theme="primary" size="sm" onClick={this.save} disabled={this.isSaving}>
                {this.isSaving ? '저장 중...' : '변경사항 저장'}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default FreeShippingWidget;
